import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    inquiry_type: 'general',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/inquiries`, formData);
      if (response.status === 200) {
        toast.success('Thank you! We will contact you shortly.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          inquiry_type: 'general',
        });
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-background" data-testid="contact-form-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-secondary text-sm font-medium tracking-widest uppercase">Get In Touch</span>
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-primary mt-4 mb-6 tracking-tight">
              Begin Your Luxury Journey
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-12">
              Whether you're seeking a second home, exploring investment opportunities, or looking for your dream
              luxury residence, our team is here to guide you every step of the way.
            </p>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 border border-secondary/30 flex items-center justify-center flex-shrink-0">
                  <Phone className="text-secondary" size={20} />
                </div>
                <div>
                  <div className="font-semibold text-primary mb-1">Phone</div>
                  <a href="tel:+919876543210" className="text-muted-foreground hover:text-secondary transition-colors duration-300">
                    +91 98203 45994
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 border border-secondary/30 flex items-center justify-center flex-shrink-0">
                  <Mail className="text-secondary" size={20} />
                </div>
                <div>
                  <div className="font-semibold text-primary mb-1">Email</div>
                  <a
                    href="mailto:info@venaraestate.com"
                    className="text-muted-foreground hover:text-secondary transition-colors duration-300"
                  >
                    info@venaraestate.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 border border-secondary/30 flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-secondary" size={20} />
                </div>
                <div>
                  <div className="font-semibold text-primary mb-1">Location</div>
                  <p className="text-muted-foreground">
                    Unity House, Charni road, Mumbai, Maharashtra<br />
                    India
                  </p>
                </div>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <div className="mt-12">
              <a
                href="https://wa.me/919820345994"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-contact-button"
                className="inline-block bg-[#25D366] text-white hover:bg-[#20BA5A] px-8 py-4 font-manrope font-bold tracking-wide uppercase text-xs transition-all duration-300"
              >
                Connect on WhatsApp
              </a>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  required
                  data-testid="contact-name-input"
                  className="w-full bg-transparent border-b border-primary/20 py-4 focus:border-primary focus:outline-none placeholder:text-primary/40 font-manrope transition-colors duration-300"
                />
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your Email"
                  required
                  data-testid="contact-email-input"
                  className="w-full bg-transparent border-b border-primary/20 py-4 focus:border-primary focus:outline-none placeholder:text-primary/40 font-manrope transition-colors duration-300"
                />
              </div>

              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Your Phone"
                  required
                  data-testid="contact-phone-input"
                  className="w-full bg-transparent border-b border-primary/20 py-4 focus:border-primary focus:outline-none placeholder:text-primary/40 font-manrope transition-colors duration-300"
                />
              </div>

              <div>
                <select
                  name="inquiry_type"
                  value={formData.inquiry_type}
                  onChange={handleChange}
                  data-testid="contact-inquiry-type-select"
                  className="w-full bg-transparent border-b border-primary/20 py-4 focus:border-primary focus:outline-none text-primary font-manrope transition-colors duration-300"
                >
                  <option value="general">General Inquiry</option>
                  <option value="second_home">Second Home</option>
                  <option value="investment">Investment</option>
                  <option value="consultation">Book Consultation</option>
                  <option value="site_visit">Schedule Site Visit</option>
                </select>
              </div>

              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Your Message"
                  rows="4"
                  required
                  data-testid="contact-message-textarea"
                  className="w-full bg-transparent border-b border-primary/20 py-4 focus:border-primary focus:outline-none placeholder:text-primary/40 font-manrope resize-none transition-colors duration-300"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-button"
                className="group bg-primary text-primary-foreground hover:bg-[#1A3F30] px-8 py-6 font-manrope tracking-wide uppercase text-xs transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Send Message'}
                <Send size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
